// Facts and copy for /parts.
//
// `verified: false` marks a claim we have not confirmed against a source we can
// stand behind. Nothing with `verified: false` is rendered, so an unverified
// claim can sit here as a record without ever reaching the page. Flip the flag
// only when the underlying fact has been checked.

export const partsPageTitle = 'Aircraft Parts Support at KPTK | Michigan Aviation'

/** Used verbatim for both `description` and `openGraph.description` so the two
 *  can never drift apart. */
export const partsDescription =
  'Aircraft parts sourcing at KPTK for piston, turboprop and Cessna Citation aircraft: on-site inventory, rotables, core exchanges and documented parts from an FAA Part 145 repair station.'

export type VerifiableClaim = { claim: string; verified: boolean }

/** Volume, capability and turnaround claims awaiting confirmation. */
export const partsClaims: VerifiableClaim[] = [
  { claim: 'thousands of maintenance events a year', verified: false },
  { claim: 'understand the fit, form, and function of every part', verified: false },
  { claim: 'Every part is inspected coming in and going out', verified: false },
  { claim: 'If you have cores, we will set up an exchange and ship the same day', verified: false },
]

export const verifiedClaims = (claims: VerifiableClaim[]) =>
  claims.filter((item) => item.verified).map((item) => item.claim)

/** What a request brings with it. Entries resting on an unverified claim above
 *  are flagged here too, so they drop out with it. */
export const partsCapabilities: VerifiableClaim[] = [
  { claim: 'Extensive on-site inventory, including high-value airframe and engine rotables', verified: true },
  { claim: 'Strong, multi-decade OEM and vendor relationships to source anything we do not stock', verified: true },
  { claim: 'Core exchanges coordinated with the paperwork the exchange program expects', verified: true },
  { claim: 'Same-day core exchanges, ready to ship wherever and whenever you need them', verified: false },
  { claim: 'Product line specialists who understand the fit, form, and function of every part', verified: false },
  { claim: 'Subcomponent and alternate-part options to minimize cost and downtime', verified: true },
  { claim: 'A consultative approach to parts replacement, included in your relationship with MAV', verified: true },
  { claim: 'Parts inspected coming in and going out, with quality inspection and shipping', verified: false },
]

/** Exchange workflow, in the same four-step shape as /parts/garmin-avionics. */
export const exchangeSteps = [
  {
    title: 'Confirm the part number and fault',
    body: 'We work from the aircraft and the maintenance task, not just the part number on the request, so the unit coming off is the one actually causing the problem.',
  },
  {
    title: 'Arrange the exchange unit and paperwork',
    body: 'Once the part is identified we coordinate the exchange unit along with the traceability paperwork that has to travel with it.',
  },
  {
    title: 'Install and return to service',
    body: 'The exchange unit is installed and the aircraft is returned to service with the maintenance records the work requires.',
  },
  {
    title: 'Return the core',
    body: 'Exchange programs expect the removed unit back. We handle the core return so the exchange closes out cleanly.',
  },
]

export const faaSupsUrl = 'https://www.faa.gov/aircraft/safety/programs/sups'
